import { useState } from "react";
import { CheckCircle, Eye, EyeOff, X } from "lucide-react";
import useAuth from "../../hooks/useAuth";

const passwordRules = [
  { label: "Mínimo 8 caracteres", test: (password) => password.length >= 8 },
  { label: "Al menos una mayúscula", test: (password) => /[A-Z]/.test(password) },
  { label: "Al menos una minúscula", test: (password) => /[a-z]/.test(password) },
  { label: "Al menos un número", test: (password) => /\d/.test(password) },
  {
    label: "Al menos un carácter especial",
    test: (password) => /[^A-Za-z0-9\s]/.test(password),
  },
];

const ChangePasswordModal = ({ onClose }) => {
  const { handleChangePassword } = useAuth();
  const [form, setForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [visibleFields, setVisibleFields] = useState({});
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isPasswordValid = passwordRules.every(({ test }) => test(form.new_password));
  const passwordsMatch =
    form.confirm_password.length > 0 && form.new_password === form.confirm_password;

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((previous) => ({ ...previous, [name]: value }));
    setError("");
  };

  const toggleVisibility = (field) => {
    setVisibleFields((previous) => ({ ...previous, [field]: !previous[field] }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    if (!isPasswordValid) {
      setError("La nueva contraseña no cumple con todos los requisitos.");
      return;
    }

    if (!passwordsMatch) {
      setError("La nueva contraseña y su confirmación no coinciden.");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await handleChangePassword(form);
      setSuccess(response.message || "La contraseña se cambió correctamente.");
      setForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (requestError) {
      const responseData = requestError.response?.data;
      const backendMessage =
        responseData?.errors?.[0] ||
        responseData?.message ||
        "No fue posible cambiar la contraseña. Intenta nuevamente.";
      setError(typeof backendMessage === "string" ? backendMessage : "No fue posible cambiar la contraseña.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderPasswordField = (name, label, autoComplete) => (
    <div className="change-password-field">
      <label htmlFor={name}>{label}</label>
      <div className="change-password-input-wrap">
        <input
          id={name}
          name={name}
          type={visibleFields[name] ? "text" : "password"}
          value={form[name]}
          onChange={updateField}
          autoComplete={autoComplete}
          required
          disabled={isSubmitting || Boolean(success)}
        />
        <button
          type="button"
          className="change-password-visibility"
          onClick={() => toggleVisibility(name)}
          aria-label={visibleFields[name] ? `Ocultar ${label.toLowerCase()}` : `Mostrar ${label.toLowerCase()}`}
          disabled={isSubmitting}
        >
          {visibleFields[name] ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>
    </div>
  );

  return (
    <div
      className="change-password-overlay"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !isSubmitting) onClose();
      }}
    >
      <section
        className="change-password-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="change-password-title"
      >
        <header className="change-password-header">
          <div>
            <h2 id="change-password-title">Cambiar contraseña</h2>
            <p>Actualiza la contraseña de tu cuenta.</p>
          </div>
          <button
            type="button"
            className="change-password-close"
            onClick={onClose}
            aria-label="Cerrar"
            disabled={isSubmitting}
          >
            <X size={20} />
          </button>
        </header>

        {success ? (
          <div className="change-password-success" role="status">
            <CheckCircle size={22} />
            <p>{success}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {renderPasswordField("current_password", "Contraseña actual", "current-password")}
            {renderPasswordField("new_password", "Nueva contraseña", "new-password")}

            <ul className="change-password-rules" aria-label="Requisitos de contraseña">
              {passwordRules.map(({ label, test }) => {
                const isValid = test(form.new_password);
                return (
                  <li className={isValid ? "is-valid" : ""} key={label}>
                    <CheckCircle size={15} aria-hidden="true" />
                    <span>{label}</span>
                  </li>
                );
              })}
            </ul>

            {renderPasswordField("confirm_password", "Confirmar nueva contraseña", "new-password")}
            {form.confirm_password && !passwordsMatch && (
              <p className="change-password-field-error">Las contraseñas no coinciden.</p>
            )}

            {error && <p className="change-password-error" role="alert">{error}</p>}

            <div className="change-password-actions">
              <button
                type="button"
                className="change-password-cancel"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="change-password-submit"
                disabled={isSubmitting || !form.current_password || !isPasswordValid || !passwordsMatch}
              >
                {isSubmitting ? "Guardando..." : "Cambiar contraseña"}
              </button>
            </div>
          </form>
        )}

        {success && (
          <div className="change-password-actions">
            <button type="button" className="change-password-submit" onClick={onClose}>
              Cerrar
            </button>
          </div>
        )}
      </section>
    </div>
  );
};

export default ChangePasswordModal;
